import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Sheet, SheetContent } from './ui/sheet';
import { useProfile } from '../contexts/ProfileContext';
import UserMenu from './UserMenu';

const LOGO_URL =
  'https://customer-assets.emergentagent.com/job_5935cf64-512e-4adb-972b-b8d08d999032/artifacts/9m3z1jc8_thumbnail_PHOTO-2025-02-05-22-08-51.jpg';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Global header for every logged-in page.
 *
 * Layout:
 *  - Left: Sturent logo (wordmark on >=480px, icon-only below). Tappable.
 *  - Right: a single rounded pill = avatar + hamburger. One tappable element.
 *  - Sticky with translucent blur.
 *
 * Logo behaviour (spec):
 *   • On /home  → soft scroll to top (no navigation)
 *   • Otherwise → navigate to /home
 *   • NEVER routes to "/" (the marketing site)
 *
 * Menu rendering:
 *   • md+ (≥768px): Popover anchored to the pill
 *   • mobile       : Sheet sliding in from the right, full-height drawer
 *   We render both wrappers at once and let CSS show the correct one for the
 *   current viewport. The pill itself is duplicated across the two wrappers
 *   but the visible pill is always singular because only one wrapper is
 *   displayed at a time.
 */
const Pill = ({ photoUrl, initial, hasBadge, onClick, variant }) => (
  <button
    type="button"
    onClick={onClick}
    className="relative inline-flex items-center gap-2 pr-3 pl-1 py-1 rounded-full bg-white border border-slate-200 shadow-soft hover:shadow-hover transition-shadow"
    data-testid={`app-header-profile-pill-${variant}`}
    aria-label="Open menu"
  >
    <span className="relative h-8 w-8 rounded-full overflow-hidden bg-amber-500 flex items-center justify-center">
      {photoUrl ? (
        <img src={photoUrl} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="text-white font-heading font-bold text-sm" data-testid="header-avatar-initial">
          {initial}
        </span>
      )}
      {hasBadge && (
        <span
          className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 bg-amber-500 rounded-full border-2 border-white"
          data-testid="header-notif-dot"
          aria-label="You have new activity"
        />
      )}
    </span>
    <Menu className="h-4 w-4 text-slate-700" />
  </button>
);

const AppHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bundle } = useProfile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const photoUrl = useMemo(() => {
    if (!bundle?.profile_photo_token) return null;
    return `${API}/api/files/${bundle.profile_photo_token}`;
  }, [bundle?.profile_photo_token]);

  const initial = (
    bundle?.user?.first_name ||
    bundle?.user?.email ||
    '?'
  ).charAt(0).toUpperCase();

  const hasBadge =
    (bundle?.counts?.saved_listings || 0) > 0 ||
    (bundle?.counts?.saved_searches || 0) > 0;

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/home');
    }
  };

  const close = () => {
    setSheetOpen(false);
    setPopoverOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 glass-nav" data-testid="app-header">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        {/* Logo — never routes to / */}
        <a
          href="/home"
          onClick={handleLogoClick}
          className="flex items-center gap-2.5"
          data-testid="app-header-logo"
        >
          <img
            src={LOGO_URL}
            alt="Sturent"
            className="h-9 w-9 rounded-xl object-cover shadow-sm"
          />
          <span className="font-heading font-bold text-lg text-slate-900 hidden min-[480px]:inline">
            Sturent
          </span>
        </a>

        {/* Mobile: pill triggers a Sheet */}
        <div className="md:hidden">
          <Pill
            photoUrl={photoUrl}
            initial={initial}
            hasBadge={hasBadge}
            onClick={() => setSheetOpen(true)}
            variant="mobile"
          />
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetContent
              side="right"
              className="w-[min(86vw,360px)] p-0 bg-background"
              data-testid="user-menu-drawer"
            >
              <UserMenu variant="drawer" onClose={close} />
            </SheetContent>
          </Sheet>
        </div>

        {/* Desktop: pill is the Popover trigger */}
        <div className="hidden md:block">
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <div>
                <Pill
                  photoUrl={photoUrl}
                  initial={initial}
                  hasBadge={hasBadge}
                  onClick={() => setPopoverOpen((v) => !v)}
                  variant="desktop"
                />
              </div>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="w-[320px] p-0 border border-slate-200 shadow-hover rounded-2xl overflow-hidden bg-background"
              data-testid="user-menu-dropdown"
            >
              <UserMenu variant="dropdown" onClose={close} />
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
